import { extendZodWithOpenApi } from "zod-openapi";

// Content blocks
// -----------------------------------------------------------------------------

import { ChatbotBlockSchema } from "block-system/blocks/Chatbot/types";
import { Dispatch, SetStateAction } from "react";
import {
  BlockIdSchema,
  ComponentBlockStyleSchema,
  ComponentBlockStyle,
} from "./base";
import { MarkdownTextBlockSchema } from "block-system/blocks/MarkdownText/schema";
import { FieldBlockSchema } from "block-system/blocks/Form/Field/schema";
import { FormBlockSchema } from "block-system/blocks/Form/Form/schema";
import { TableBlockSchema } from "block-system/blocks/Table/schema";
import { KanbanBlockSchema } from "block-system/blocks/Kanban/schema";
import { LinksBlockSchema } from "block-system/blocks/Links/Links/schema";
import { LinkCardSchema } from "block-system/blocks/Links/LinksCard/schema";
import { MediaBlockSchema } from "block-system/blocks/Media/schema";
import { DividerBlockSchema } from "block-system/blocks/Divider/schema";
import { EmbedBlockSchema } from "block-system/blocks/Embed/schema";
import { ButtonBlockSchema } from "block-system/blocks/Button/schema";
import { ChecklistBlockSchema } from "block-system/blocks/Checklist/schema";
import { LayoutBlockSchema } from "block-system/blocks/Layout/schema";
import { StripePaymentBlockSchema } from "block-system/blocks/StripePayment/schema";
import { LoginFormBlockSchema } from "block-system/blocks/LoginForm/schema";
import { AIGeneratedBlockSchema } from "block-system/blocks/AIGenerated/schema";
import { toInputSchema, toOutputSchema } from "@/server/schemas/util";
import { z } from "zod";

extendZodWithOpenApi(z);

export { BlockIdSchema, ComponentBlockStyleSchema };
export type { ComponentBlockStyle };

// Root level components
// -----------------------------------------------------------------------------

const ComponentBlockSchema = z.union([
  ChatbotBlockSchema,
  FormBlockSchema,
  LinksBlockSchema,
  MediaBlockSchema,
  TableBlockSchema,
  LayoutBlockSchema,
]);

export type ComponentBlock = z.infer<typeof ComponentBlockSchema>;

export type ComponentBlockTypes = ComponentBlock["type"];

// All blocks
// -----------------------------------------------------------------------------
export const ContentBlockSchema = z
  .union([
    MarkdownTextBlockSchema,
    FieldBlockSchema,
    FormBlockSchema,
    TableBlockSchema,
    KanbanBlockSchema,
    LinksBlockSchema,
    LinkCardSchema,
    ChatbotBlockSchema,
    MediaBlockSchema,
    DividerBlockSchema,
    EmbedBlockSchema,
    ButtonBlockSchema,
    ChecklistBlockSchema,
    LayoutBlockSchema,
    StripePaymentBlockSchema,
    LoginFormBlockSchema,
    AIGeneratedBlockSchema,
  ])
  .openapi({ ref: "InterfaceBlock" });

export type ContentBlock = z.infer<typeof ContentBlockSchema>;

type ContentBlockType = ContentBlock["type"];

export type ContentBlockTypes = ContentBlock["type"];

export const ContentBlocksSchema = z.array(ContentBlockSchema);

export type ContentBlocks = z.infer<typeof ContentBlocksSchema>;

export const ContentBlockTypeNames: Record<ContentBlockTypes, string> = {
  "divider-block": "Divider",
  "field-block": "Field",
  "form-block": "Form",
  "kanban-block": "Kanban",
  "links-block": "Links",
  "links-card-block": "LinksCard",
  "markdown-text-block": "MarkdownText",
  "media-block": "Media",
  "table-listing-block": "TableListing",
  "embed-block": "Embed",
  "button-block": "Button",
  "checklist-block": "Checklist",
  "chatbot-block": "Chatbot",
  "layout-block": "Layout",
  "stripe-payment-block": "Stripe Payment",
  "login-form-block": "Login Form",
  "ai-generated-block": "AI Generated",
};
/**
 * Problem: Without any additional checks, the `contentBlockTypes` might contain only a subset of the
 * literals for types listed in `ContentBlockTypes` union.
 *
 * Solution: Verify via TypeScript that `contentBlockTypes` contains all the items in `ContentBlockTypes`
 */
const contentBlockTypes = [
  "divider-block",
  "field-block",
  "markdown-text-block",
  "form-block",
  "table-listing-block",
  "kanban-block",
  "links-block",
  "links-card-block",
  "chatbot-block",
  "media-block",
  "embed-block",
  "button-block",
  "checklist-block",
  "layout-block",
  "stripe-payment-block",
  "login-form-block",
  "ai-generated-block",
] as const satisfies Array<ContentBlockType>;

export type BlockTypes = (typeof contentBlockTypes)[number];

export const BlockTypeSchema = z.enum(contentBlockTypes);

type ContainsAllTheBlockTypes =
  ContentBlockType extends (typeof contentBlockTypes)[number] ? true : false;
/**
 * If you see an error here, it means that `ContentBlockType` has a new item that is not listed in `contentBlockTypes`.
 */
// @ts-expect-error
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _verification: ContainsAllTheBlockTypes = false;

export type ComponentBlockFlag = "Beta" | "Experimental" | "Pro" | "New";

export const ComponentBlockFlags: Partial<
  Record<ContentBlockType, ComponentBlockFlag>
> = {
  "chatbot-block": "Beta",
  "ai-generated-block": "Experimental",
  "embed-block": "Pro",
  "layout-block": "Pro",
  "stripe-payment-block": "Pro",
};

// Page content
// -----------------------------------------------------------------------------

export const CURRENT_CONTENT_VERSION = "2";
type ContentSchemaType = z.ZodObject<{
  version: z.ZodEnum<[typeof CURRENT_CONTENT_VERSION]>;
  blocks: typeof ContentBlocksSchema;
}>;

export const ContentSchema: ContentSchemaType = z
  .object({
    version: z.enum([CURRENT_CONTENT_VERSION]),
    blocks: ContentBlocksSchema,
  })
  .describe("The content of the page. The page consists of blocks.");

export const ContentInputSchema: ContentSchemaType =
  toInputSchema(ContentSchema);

export const ContentOutputSchema: ContentSchemaType =
  toOutputSchema(ContentSchema);

export type Content = z.infer<typeof ContentSchema>;

export type MutateBlocks = Dispatch<SetStateAction<ContentBlocks>>;
